// filepath: apps/web/src/admin/organization-units/OrganizationUnitForm.tsx
//
// ISS-517 — shared organization-unit create/edit form component.
//
// Two exports, both presentational + controlled:
//
//   <OrganizationUnitForm mode="create" ... />
//     name + code + legal-entity <select> + parent <select>
//   <OrganizationUnitForm mode="edit"   ... />
//     name + code ONLY.
//
// WHY edit carries no pickers: the Go update route
// `PATCH /api/directory/organization-units/{id}`
// (`internal/directory/organization_unit/handlers.go:110-115`) reads
// exactly four fields — `row_version`, `name`, `code`, `clear_code`.
// There is NO parent field on it, and the repository `Update`
// (`repository.go:113-158`) writes only name / code. Reparenting is
// reachable ONLY through the dedicated
// `POST /{id}/move` route, which is rendered as its own section
// (`<OrganizationUnitMoveForm>` below) so the two OCC tokens travel
// on their own requests.
//
// BA ruling note 18335 (D3): `legal_entity_id` is a USER-SELECTABLE
// business field on create, not an INV-1 security scope. When the
// tenant has exactly one legal entity the <select> is preselected and
// `disabled` (read-only) — there is nothing else to choose.
//
// BA ruling note 18333 (D2): cycle detection is SERVER-AUTHORITATIVE.
// The parent picker below is a flat <select> with NO descendant walk
// and NO client-side cycle test. A server 409 `cycle_detected` is
// mapped to `parentError` by the caller and rendered here.
//
// INV-2: no server-joined display field is rendered on the unit
// itself — the legal-entity NAME comes from the picker's own option
// list, which the server emitted. Nothing is synthesised.
// INV-9: no dangerouslySetInnerHTML; every value flows through JSX
// text nodes or <option value>.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Card } from "@/design-system/Card";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { useDirtyTrackedFields } from "@/lib/useDirtyTrackedFields";

export interface OrganizationUnitFormValues {
  legal_entity_id: string;
  parent_organization_unit_id: string | null;
  name: string;
  code: string;
}

/**
 * The shape handed to `useDirtyTrackedFields`. A `type` alias, not the
 * exported `interface` above: only the former gets an implicit index
 * signature, which the hook's `T extends Record<string, unknown>`
 * bound needs. The server's `null` parent is "" here, per field, because
 * the picker is a string-valued `<select>` — presentation
 * normalisation, NOT the sibling coupling this replaces.
 */
type OrganizationUnitFormFields = {
  legal_entity_id: string;
  parent_organization_unit_id: string;
  name: string;
  code: string;
};

/** A minimal picker option. Mirrors `PositionForm.orgUnitCandidates`. */
export interface OrganizationUnitCandidate {
  id: string;
  name: string;
}

export interface OrganizationUnitFormProps {
  mode: "create" | "edit";
  initial: OrganizationUnitFormValues;
  /**
   * Legal-entity options from `listLegalEntities()`. Rendered as
   * `name` + `id` only (INV-5). When exactly ONE option exists the
   * <select> is preselected and disabled per BA ruling D3.
   */
  legalEntityCandidates?: OrganizationUnitCandidate[];
  /**
   * Parent-unit options from `listOrganizationUnits()`. Rendered as
   * `name` + `id` only. Flat list — NO descendant filtering is done
   * client-side; the server is the cycle authority (BA ruling D2).
   */
  parentCandidates?: OrganizationUnitCandidate[];
  onSubmit: (values: OrganizationUnitFormValues) => Promise<void>;
  onCancel?: () => void;
  submitting?: boolean;
  error?: string | null;
  /** Field-level error on the name input. */
  nameError?: string | null;
  /** Field-level error on the legal-entity select. */
  legalEntityError?: string | null;
  /** Field-level error on the parent select (server 409 cycle). */
  parentError?: string | null;
  submitLabel: string;
  loadingLabel: string;
  heading: string;
}

/**
 * Legal-entity picker UX (BA ruling D3).
 *
 * Returns the <select> options plus whether the control must be
 * read-only. With exactly one candidate there is nothing to choose,
 * so the control is preselected and disabled. With zero candidates
 * the select is disabled too — create cannot succeed without a legal
 * entity (the Go create handler reads it as a non-pointer
 * uuid.UUID, handlers.go:63) and the caller surfaces the error.
 */
export function legalEntityPickerState(
  candidates: readonly OrganizationUnitCandidate[],
): { options: OrganizationUnitCandidate[]; disabled: boolean } {
  return {
    options: [...candidates],
    disabled: candidates.length <= 1,
  };
}

export function OrganizationUnitForm({
  mode,
  initial,
  legalEntityCandidates,
  parentCandidates,
  onSubmit,
  onCancel,
  submitting,
  error,
  nameError,
  legalEntityError,
  parentError,
  submitLabel,
  loadingLabel,
  heading,
}: OrganizationUnitFormProps): React.ReactElement {
  const { t } = useTranslation();

  // BA ruling D3 — preselect read-only when exactly one legal entity
  // exists. `legalEntityPickerState` disables the control in that case
  // so the operator cannot change it.
  const leCandidates = legalEntityCandidates ?? [];
  const leState = legalEntityPickerState(leCandidates);
  const preselectedLegalEntityId =
    leState.disabled && leState.options.length === 1
      ? (leState.options[0]?.id ?? "")
      : null;

  // ISS-805 — per-field dirty tracking replaces the whole-state re-seed effect
  // that used to sit here on [initial.name, initial.code,
  // initial.legal_entity_id, initial.parent_organization_unit_id]. That effect
  // reset ALL FOUR fields when ANY ONE dep moved, so a code changing in
  // another session wiped the name the operator was typing. Each field now
  // re-seeds only on its OWN server value, and never once it has been edited
  // (see lib/useDirtyTrackedFields for the BA ruling).
  //
  // The BA-D3 preselect rides in as `legal_entity_id`'s SERVER value rather
  // than as a `set()` call. It is an internal seed, not a user edit, and the
  // hook has exactly one mutation entry point — `set`, which marks the field
  // dirty forever. Writing it as a server value keeps the picker
  // preselect-disabled behaviour AND leaves the field re-seedable (rule 2);
  // routing it through `set` would pin it as "edited" by an operator who
  // never touched it. The dep is the CANDIDATE LIST (the picker's own feed,
  // not a sibling form field's value), so rule 3 is untouched.
  //
  // `recordKey` is a primitive, never the `initial` object (a new identity
  // every render would re-seed on every render). This form carries no record
  // id, so its identity IS its mount plus its mode: the edit screen keys it
  // by row id, and pointing the route at a different unit remounts the form,
  // which is rule 1's "different record" path.
  const fields = useDirtyTrackedFields<OrganizationUnitFormFields>(
    {
      name: initial.name,
      code: initial.code,
      legal_entity_id: preselectedLegalEntityId ?? initial.legal_entity_id,
      parent_organization_unit_id: initial.parent_organization_unit_id ?? "",
    },
    `org-unit:${mode}`,
  );

  // BA rule 4 — a non-blocking notice, never a dialog: when the server value
  // moved underneath a field the operator HAS edited, their text is kept and
  // the server's value is shown beside it. Dismiss clears it per field.
  const conflictFor = (
    id: string,
    label: string,
    field: {
      serverConflictValue: string | undefined;
      acknowledgeServerConflict: () => void;
    },
  ): {
    id: string;
    label: string;
    value: string;
    dismiss: () => void;
  } | null =>
    field.serverConflictValue === undefined
      ? null
      : {
          id,
          label,
          value: field.serverConflictValue,
          dismiss: field.acknowledgeServerConflict,
        };

  const serverConflicts = [
    conflictFor(
      "name",
      t("directory.organization_units.form.name", "Name"),
      fields.name,
    ),
    conflictFor(
      "code",
      t("directory.organization_units.form.code", "Code"),
      fields.code,
    ),
    conflictFor(
      "legal_entity_id",
      t("directory.organization_units.form.legal_entity", "Legal entity"),
      fields.legal_entity_id,
    ),
    conflictFor(
      "parent_organization_unit_id",
      t("directory.organization_units.form.parent", "Parent"),
      fields.parent_organization_unit_id,
    ),
  ].filter((c): c is NonNullable<typeof c> => c !== null);

  const isCreate = mode === "create";
  const canSubmit =
    !submitting &&
    fields.name.value.trim().length > 0 &&
    (!isCreate || fields.legal_entity_id.value.trim().length > 0);

  return (
    <section data-testid="organization-unit-form-screen">
      <header>
        <h1 id="organization-unit-form-title">{heading}</h1>
      </header>
      {error ? (
        // design-adherence-ignore: <Banner> doesn't accept data-testid
        // in every build; hook the test id on a wrapping span.
        <span data-testid="organization-unit-form-error">
          <Banner tone="danger">
            {t(
              "directory.organization_units.form.error",
              "Error: {{message}}",
              {
                message: error,
              },
            )}
          </Banner>
        </span>
      ) : null}
      {serverConflicts.length > 0 ? (
        <div data-testid="organization-unit-form-server-conflicts">
          {serverConflicts.map((c) => (
            <Banner key={c.id} tone="warning" onDismiss={c.dismiss}>
              {t(
                "directory.organization_units.form.server_conflict",
                "{{field}} changed on the server while you were editing. Your text was kept — the server now holds: {{value}}",
                { field: c.label, value: c.value },
              )}
            </Banner>
          ))}
        </div>
      ) : null}
      <Card>
        <form
          aria-labelledby="organization-unit-form-title"
          onSubmit={async (e) => {
            e.preventDefault();
            await onSubmit({
              legal_entity_id: fields.legal_entity_id.value.trim(),
              parent_organization_unit_id:
                fields.parent_organization_unit_id.value || null,
              name: fields.name.value.trim(),
              code: fields.code.value.trim(),
            });
          }}
          data-testid="organization-unit-form"
        >
          <div>
            <label htmlFor="organization-unit-form-name">
              {t("directory.organization_units.form.name", "Name")}
            </label>
            <input
              id="organization-unit-form-name"
              type="text"
              required
              minLength={1}
              maxLength={255}
              value={fields.name.value}
              onChange={(e) => fields.name.set(e.target.value)}
              aria-invalid={nameError ? true : undefined}
              aria-describedby={
                nameError ? "organization-unit-form-name-error" : undefined
              }
              data-testid="organization-unit-form-name-input"
            />
            {nameError ? (
              <p
                role="alert"
                id="organization-unit-form-name-error"
                data-testid="organization-unit-form-name-error"
              >
                {nameError}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="organization-unit-form-code">
              {t("directory.organization_units.form.code", "Code")}
            </label>
            <input
              id="organization-unit-form-code"
              type="text"
              maxLength={64}
              value={fields.code.value}
              onChange={(e) => fields.code.set(e.target.value)}
              data-testid="organization-unit-form-code-input"
            />
          </div>

          {isCreate ? (
            <div>
              <label htmlFor="organization-unit-form-legal-entity">
                {t(
                  "directory.organization_units.form.legal_entity",
                  "Legal entity",
                )}
              </label>
              {/* INV-5: options render `name` only — never
                  registration_number / tax_id / address_sha256. */}
              <select
                id="organization-unit-form-legal-entity"
                value={fields.legal_entity_id.value}
                disabled={leState.disabled}
                required
                onChange={(e) => fields.legal_entity_id.set(e.target.value)}
                aria-invalid={legalEntityError ? true : undefined}
                aria-describedby={
                  legalEntityError
                    ? "organization-unit-form-legal-entity-error"
                    : undefined
                }
                data-testid="organization-unit-form-legal-entity-select"
              >
                <option value="">
                  {t(
                    "directory.organization_units.form.no_legal_entity",
                    "(select a legal entity)",
                  )}
                </option>
                {leState.options.map((le) => (
                  <option key={le.id} value={le.id}>
                    {le.name}
                  </option>
                ))}
              </select>
              {legalEntityError ? (
                <p
                  role="alert"
                  id="organization-unit-form-legal-entity-error"
                  data-testid="organization-unit-form-legal-entity-error"
                >
                  {legalEntityError}
                </p>
              ) : null}
            </div>
          ) : null}

          {isCreate ? (
            <div>
              <label htmlFor="organization-unit-form-parent">
                {t("directory.organization_units.form.parent", "Parent")}
              </label>
              {/* BA ruling D2: a FLAT picker. No descendant walk,
                  no client-side cycle test — the server 409 is the
                  only authority and it arrives as `parentError`. */}
              <select
                id="organization-unit-form-parent"
                value={fields.parent_organization_unit_id.value}
                onChange={(e) =>
                  fields.parent_organization_unit_id.set(e.target.value)
                }
                aria-invalid={parentError ? true : undefined}
                aria-describedby={
                  parentError
                    ? "organization-unit-form-parent-error"
                    : undefined
                }
                data-testid="organization-unit-form-parent-select"
              >
                <option value="">
                  {t(
                    "directory.organization_units.form.no_parent",
                    "(no parent — root)",
                  )}
                </option>
                {(parentCandidates ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {parentError ? (
                <p
                  role="alert"
                  id="organization-unit-form-parent-error"
                  data-testid="organization-unit-form-parent-error"
                >
                  {parentError}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="organization-unit-form-actions">
            {/* design-adherence-ignore: <Button> doesn't accept
                data-testid on every build; wrap to keep the test hook. */}
            <span data-testid="organization-unit-form-submit">
              <Button type="submit" variant="primary" disabled={!canSubmit}>
                {submitting ? loadingLabel : submitLabel}
              </Button>
            </span>
            {onCancel ? (
              <span data-testid="organization-unit-form-cancel">
                <Button type="button" variant="secondary" onClick={onCancel}>
                  {t("directory.organization_units.form.cancel", "Cancel")}
                </Button>
              </span>
            ) : null}
          </div>
        </form>
      </Card>
    </section>
  );
}

// ─── Move (dedicated POST /{id}/move route) ──────────────────────────────────

export interface OrganizationUnitMoveFormProps {
  /** Current parent of the moved unit (null when it is a root). */
  currentParentId: string | null;
  parentCandidates: OrganizationUnitCandidate[];
  onMove: (nextParentId: string | null) => Promise<void>;
  onCancel?: () => void;
  moving?: boolean;
  /** Server 409 `cycle_detected` mapped to this slot by the caller. */
  parentError?: string | null;
}

/**
 * The reparent control. Separate from <OrganizationUnitForm> because
 * the Go backend exposes reparenting on its own route with its own OCC
 * token (`POST /{id}/move` with `{row_version,
 * parent_organization_unit_id}`, handlers.go:151-154) — a reparent is
 * a different mutation from a rename, and the two must not share one
 * request.
 *
 * BA ruling D2: NO client-side cycle detection. The picker lists every
 * unit (including the moved unit's own descendants); selecting an
 * illegal parent is answered by the server with 409 +
 * `code: "cycle_detected"`, which the caller renders into
 * `parentError`. The server's recursive CTE
 * (`repository.go:165-203`) is the single authority.
 */
export function OrganizationUnitMoveForm({
  currentParentId,
  parentCandidates,
  onMove,
  onCancel,
  moving,
  parentError,
}: OrganizationUnitMoveFormProps): React.ReactElement {
  const { t } = useTranslation();
  const [parentId, setParentId] = React.useState<string>(currentParentId ?? "");

  React.useEffect(() => {
    setParentId(currentParentId ?? "");
  }, [currentParentId]);

  return (
    <Card>
      <form
        aria-label={t("directory.organization_units.move.title", "Move unit")}
        onSubmit={async (e) => {
          e.preventDefault();
          await onMove(parentId || null);
        }}
        data-testid="organization-unit-move-form"
      >
        <div>
          <label htmlFor="organization-unit-move-parent">
            {t("directory.organization_units.move.parent_label", "New parent")}
          </label>
          <select
            id="organization-unit-move-parent"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            aria-invalid={parentError ? true : undefined}
            aria-describedby={
              parentError ? "organization-unit-move-parent-error" : undefined
            }
            data-testid="organization-unit-move-parent-select"
          >
            <option value="">
              {t(
                "directory.organization_units.form.no_parent",
                "(no parent — root)",
              )}
            </option>
            {parentCandidates.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {parentError ? (
            <p
              role="alert"
              id="organization-unit-move-parent-error"
              data-testid="organization-unit-move-parent-error"
            >
              {parentError}
            </p>
          ) : null}
        </div>
        <div className="organization-unit-move-actions">
          {/* design-adherence-ignore: <Button> test hook via wrapper. */}
          <span data-testid="organization-unit-move-submit">
            <Button type="submit" variant="primary" disabled={moving}>
              {moving
                ? t("directory.organization_units.move.moving", "Moving…")
                : t("directory.organization_units.move.submit", "Move")}
            </Button>
          </span>
          {onCancel ? (
            <span data-testid="organization-unit-move-cancel">
              <Button type="button" variant="secondary" onClick={onCancel}>
                {t("directory.organization_units.move.cancel", "Cancel")}
              </Button>
            </span>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
